/**
 * @packageDocumentation
 * @category Util
 */

export enum SupportedLanguageEnum {
  english = 'english',
  french = 'french',
  estonian = 'estonian',
  russian = 'russian',
  german = 'german',
  spanish = 'spanish',
  portuguese = 'portuguese',
  arabic = 'arabic',
  swahili = 'swahili',
  hindi = 'hindi',
  indonesian = 'indonesian',
  filipino = 'filipino',
  vietnamese = 'vietnamese',
  chinese = 'chinese',
  korean = 'korean',
  japanese = 'japanese',
  bengali = 'bengali',
  amharic = 'amharic',
}

export enum SupportedLanguageCodeEnum {
  en = 'en',
  fr = 'fr',
  et = 'et',
  ru = 'ru',
  de = 'de',
  es = 'es',
  pt = 'pt',
  ar = 'ar',
  sw = 'sw',
  hi = 'hi',
  id = 'id',
  fil = 'fil',
  vi = 'vi',
  zh = 'zh',
  ko = 'ko',
  ja = 'ja',
  bn = 'bn',
  am = 'am',
}

export type SupportedLanguage = keyof typeof SupportedLanguageEnum;
export type SupportedLanguageCode = keyof typeof SupportedLanguageCodeEnum;
export type Translation = {
  [key in SupportedLanguage]: string;
};
export type TranslationByLanguageCode = {
  [key in SupportedLanguageCode]: string;
};

/**-------------------------------------------------------------------------------------------------------------------------
 *                                                 getLanguageFromLocale
 *--------------------------------------------------------------------------------------------------------------------------
 *
 */
export function getLanguageFromLocale(locale?: string): SupportedLanguage {
  if (!locale || typeof locale != 'string') return 'english';

  const allLocales = locale.split(',');
  let language;

  for (let i = 0, len = allLocales.length; i < len; i++) {
    language = getLanguage(allLocales[i].split(';')[0]);
    if (language) return language;
  }

  return 'english';

  function getLanguage(localeKey: string): SupportedLanguage {
    switch (localeKey) {
      case 'en':
      case 'en-US':
        return 'english';
      case 'fr':
      case 'fr-CA':
      case 'fr-FR':
        return 'french';
      case 'ru':
      case 'ru_RU':
        return 'russian';
      case 'et':
      case 'et-EE':
        return 'estonian';
      case 'de':
      case 'de-DE':
        return 'german';
      case 'es':
      case 'es-ES':
      case 'es-MX':
        return 'spanish';
      case 'pt':
      case 'pt-BR':
      case 'pt-PT':
        return 'portuguese';
      case 'ar':
      case 'ar-SA':
      case 'ar-EG':
        return 'arabic';
      case 'sw':
      case 'sw-KE':
        return 'swahili';
      case 'hi':
      case 'hi-IN':
        return 'hindi';
      case 'id':
      case 'id-ID':
        return 'indonesian';
      case 'fil':
      case 'fil-PH':
        return 'filipino';
      case 'vi':
      case 'vi-VN':
        return 'vietnamese';
      case 'zh':
      case 'zh-CN':
      case 'zh-Hans':
        return 'chinese';
      case 'ko':
      case 'ko-KR':
        return 'korean';
      case 'ja':
      case 'ja-JP':
        return 'japanese';
      case 'bn':
      case 'bn-BD':
        return 'bengali';
      case 'am':
      case 'am-ET':
        return 'amharic';

      default:
        return 'english';
    }
  }
}

/**-------------------------------------------------------------------------------------------------------------------------
 *                                                 getLanguageName
 *--------------------------------------------------------------------------------------------------------------------------
 *
 */
export function getLanguageName(language: SupportedLanguage | SupportedLanguageCode) {
  switch (language) {
    case 'en':
    case 'english':
      return 'English';
    case 'fr':
    case 'french':
      return 'Français';
    case 'et':
    case 'estonian':
      return 'Eesti';
    case 'de':
    case 'german':
      return 'Deutsch';
    case 'ru':
    case 'russian':
      return 'Русский';
    case 'es':
    case 'spanish':
      return 'Español';
    case 'pt':
    case 'portuguese':
      return 'Português';
    case 'ar':
    case 'arabic':
      return 'العربية';
    case 'sw':
    case 'swahili':
      return 'Kiswahili';
    case 'hi':
    case 'hindi':
      return 'हिन्दी';
    case 'id':
    case 'indonesian':
      return 'Bahasa Indonesia';
    case 'fil':
    case 'filipino':
      return 'Filipino';
    case 'vi':
    case 'vietnamese':
      return 'Tiếng Việt';
    case 'zh':
    case 'chinese':
      return '中文';
    case 'ko':
    case 'korean':
      return '한국어';
    case 'ja':
    case 'japanese':
      return '日本語';
    case 'bn':
    case 'bengali':
      return 'বাংলা';
    case 'am':
    case 'amharic':
      return 'አማርኛ';
  }
}

/**-------------------------------------------------------------------------------------------------------------------------
 *                                                 getLanguageCode
 *--------------------------------------------------------------------------------------------------------------------------
 *
 */
export function getLanguageCode(language: SupportedLanguage) {
  switch (language) {
    case 'english':
      return 'en';
    case 'french':
      return 'fr';
    case 'estonian':
      return 'et';
    case 'german':
      return 'de';
    case 'russian':
      return 'ru';
    case 'spanish':
      return 'es';
    case 'portuguese':
      return 'pt';
    case 'arabic':
      return 'ar';
    case 'swahili':
      return 'sw';
    case 'hindi':
      return 'hi';
    case 'indonesian':
      return 'id';
    case 'filipino':
      return 'fil';
    case 'vietnamese':
      return 'vi';
    case 'chinese':
      return 'zh';
    case 'korean':
      return 'ko';
    case 'japanese':
      return 'ja';
    case 'bengali':
      return 'bn';
    case 'amharic':
      return 'am';
  }
}

/**-------------------------------------------------------------------------------------------------------------------------
 *                                                 getLanguageLogo
 *--------------------------------------------------------------------------------------------------------------------------
 *
 */
export function getLanguageLogo(language: SupportedLanguage) {
  switch (language) {
    case 'english':
      return '🇺🇸';
    case 'french':
      return '🇫🇷';
    case 'estonian':
      return '🇪🇪';
    case 'russian':
      return '🇷🇺';
    case 'german':
      return '🇩🇪';
    case 'spanish':
      return '🇪🇸';
    case 'portuguese':
      return '🇧🇷';
    case 'arabic':
      return '🇸🇦';
    case 'swahili':
      return '🇰🇪';
    case 'hindi':
      return '🇮🇳';
    case 'indonesian':
      return '🇮🇩';
    case 'filipino':
      return '🇵🇭';
    case 'vietnamese':
      return '🇻🇳';
    case 'chinese':
      return '🇨🇳';
    case 'korean':
      return '🇰🇷';
    case 'japanese':
      return '🇯🇵';
    case 'bengali':
      return '🇧🇩';
    case 'amharic':
      return '🇪🇹';
  }
}
